/**
 * Anchor required (APP-ENTITLEMENT-001, Q-AE-15): no app is held without an anchor, and only beside an anchor it
 * attaches to. An account holds at most ONE anchor.
 *
 * @module @puffin-core/app-catalog/rules/anchor
 */

'use strict';

const { getApp, isAnchorKey, attachesTo } = require('../catalog');
const { APP_KIND } = require('../vocabulary');
const { APP_CATALOG_REFUSAL, AppCatalogRefusal } = require('../refusal');

/**
 * The anchor among a set of held app keys, or null.
 *
 * @param {Iterable<string>} heldAppKeys
 * @returns {?string}
 * @throws {TypeError} when more than one anchor is held — a state the engine never writes.
 */
function anchorOf(heldAppKeys) {
  let anchor = null;
  for (const key of heldAppKeys) {
    if (!isAnchorKey(key)) continue;
    if (anchor !== null && anchor !== key) {
      throw new TypeError(`an account holds one anchor, found ${anchor} and ${key}`);
    }
    anchor = key;
  }
  return anchor;
}

/**
 * Why `appKey` may NOT be added beside `heldAppKeys`, or null when it may.
 *
 * - `APP_UNKNOWN` — not a catalog key.
 * - `APP_NOT_SELLABLE` — the row is not offered (ProPuffin while "coming soon").
 * - `ANCHOR_REQUIRED` — an app with no anchor held (adding an ANCHOR is always allowed when none is held).
 * - `APP_NOT_ATTACHABLE` — the app does not attach to the held anchor, or a second anchor.
 *
 * @param {{heldAppKeys: Iterable<string>, appKey: string}} params
 * @returns {?string} one of `APP_CATALOG_REFUSAL` or null.
 */
function addRefusal({ heldAppKeys, appKey }) {
  const entry = getApp(appKey);
  if (entry === null) return APP_CATALOG_REFUSAL.APP_UNKNOWN;
  if (!entry.isSellable) return APP_CATALOG_REFUSAL.APP_NOT_SELLABLE;
  const anchor = anchorOf(heldAppKeys);
  if (entry.kind === APP_KIND.ANCHOR) {
    return anchor === null || anchor === appKey ? null : APP_CATALOG_REFUSAL.APP_NOT_ATTACHABLE;
  }
  if (anchor === null) return APP_CATALOG_REFUSAL.ANCHOR_REQUIRED;
  return attachesTo(appKey, anchor) ? null : APP_CATALOG_REFUSAL.APP_NOT_ATTACHABLE;
}

/**
 * `addRefusal` as a throw, for the engine's write path.
 *
 * @param {{heldAppKeys: Iterable<string>, appKey: string}} params
 * @throws {AppCatalogRefusal}
 */
function assertCanAdd({ heldAppKeys, appKey }) {
  const code = addRefusal({ heldAppKeys, appKey });
  if (code !== null) {
    throw new AppCatalogRefusal(code, { appKey, anchorAppKey: anchorOf(heldAppKeys) });
  }
}

module.exports = { anchorOf, addRefusal, assertCanAdd };
